// NIGHT-DIRECTSTAFF-2026-10-25d §5: the candidate's e-sign audit trail, on their Onboarding tab -- read from the onboard portal's
// staff-only /api/esign-audit (the portal checks the staff session against its row-level security first): each signed form with
// signed-at (UTC and Eastern), the session, the IP as a hash, the content hash, the browser; "Download PDF summary" (no form contents).
import React, { useState } from 'react';

const btn = (bg, fg, off) => ({ background: bg, color: fg, borderRadius: 8, padding: '0 14px', minHeight: 44, fontSize: 13, fontWeight: 700, border: 0, cursor: off ? 'not-allowed' : 'pointer', opacity: off ? 0.5 : 1 });
const cell = { padding: '6px', borderTop: '1px solid #f1f5f9', fontSize: 12.5, textAlign: 'left', verticalAlign: 'top', overflowWrap: 'anywhere' };
const WORDS = { 'esign:none': 'No onboarding packet for them yet, so nothing signed.', 'esign:sign_in': 'Sign in again to read it.', 'esign:unavailable': 'The portal could not read it just now. Try again in a minute.' };

/** candidate; baseUrl (the onboard portal); token() -> Promise<the staff session token>; fetchFn */
export default function EsignAudit({ candidate, baseUrl, token, fetchFn = (...a) => fetch(...a) }) {
  const [data, setData] = useState(null);       // { rows } | { error }
  const [busy, setBusy] = useState(false);
  const call = async (format) => {
    const t = await token();
    return fetchFn(`${String(baseUrl || '').replace(/\/$/, '')}/api/esign-audit`, { method: 'POST', headers: { 'content-type': 'application/json', authorization: `Bearer ${t}` },
      body: JSON.stringify({ candidate_id: candidate.id, format }) });
  };
  const load = async () => {
    setBusy(true);
    try { const r = await call('json'); const out = await r.json().catch(() => ({})); setData(r.ok ? out : { error: WORDS[out.error] || 'The audit trail could not be read.' }); }
    catch (e) { setData({ error: 'The portal could not be reached.' }); } finally { setBusy(false); }
  };
  const pdf = async () => {
    setBusy(true);
    try {
      const r = await call('pdf');
      if (!r.ok) { setData({ error: 'The PDF could not be made.' }); return; }
      const url = URL.createObjectURL(await r.blob());
      const a = document.createElement('a'); a.href = url; a.download = `esign-audit-${candidate.id}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } finally { setBusy(false); }
  };
  return (
    <section data-testid="esign-audit" aria-labelledby="esign-audit-title" className="card" style={{ padding: 14, marginTop: 12, minWidth: 0 }}>
      <h4 id="esign-audit-title" style={{ fontSize: 14, fontWeight: 800, margin: '0 0 4px', color: '#0f172a' }}>E-sign audit trail</h4>
      <p style={{ fontSize: 12.5, color: '#475569', margin: '0 0 8px' }}>Each signed form: when, the session, the IP as a hash, the content hash stored at signing. No form contents.</p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="nb" data-testid="esign-audit-load" disabled={busy} onClick={load} style={btn('#0f172a', 'white', busy)}>{data && data.rows ? 'Reload' : 'Show the trail'}</button>
        <button className="nb" data-testid="esign-audit-pdf" disabled={busy} onClick={pdf} style={btn('#f1f5f9', '#0f172a', busy)}>Download PDF summary</button>
      </div>
      {data && data.error && <p data-testid="esign-audit-error" role="alert" style={{ fontSize: 13, color: '#b91c1c', margin: '8px 0 0' }}>{data.error}</p>}
      {data && data.rows && (data.rows.length === 0 ? <p style={{ fontSize: 13, color: '#475569', margin: '8px 0 0' }}>Nothing signed yet.</p> : (
        <div style={{ marginTop: 8, minWidth: 0 }}>
          <ul data-testid="esign-audit-table" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {data.rows.map((r, i) => (
              <li key={`${r.form}-${r.session_id}-${i}`} data-testid={`esign-row-${r.form}`} style={{ ...cell, display: 'grid', gap: 2, overflowWrap: 'anywhere' }}>
                <span style={{ fontWeight: 700 }}>{r.label}</span>
                <span>Signed {r.eastern} <span style={{ color: '#475569', fontSize: 11.5 }}>({r.utc})</span> · session {r.session_id}</span>
                <span>IP hash <code>{r.ip_hash || 'not recorded'}</code></span>
                <span>Content hash <code style={{ fontSize: 11 }}>{r.content_words}</code></span>
              </li>))}
          </ul>
          <p style={{ fontSize: 11.5, color: '#475569', margin: '6px 0 0' }}>{data.version}</p>
        </div>))}
    </section>
  );
}
